import { useState, useCallback, useRef, useEffect } from 'react';

interface UseUndoRedoOptions {
  maxHistory?: number;
  debounceMs?: number;
}

interface UseUndoRedoReturn<T> {
  /** Push a new state to the history */
  pushState: (state: T) => void;
  /** Undo to the previous state */
  undo: () => T | undefined;
  /** Redo to the next state */
  redo: () => T | undefined;
  /** Check if undo is available */
  canUndo: boolean;
  /** Check if redo is available */
  canRedo: boolean;
  /** Clear all history */
  clear: () => void;
  /** Get current history size */
  historySize: number;
}

/**
 * Hook for managing undo/redo history
 */
export function useUndoRedo<T>(
  initialState: T,
  options: UseUndoRedoOptions = {}
): UseUndoRedoReturn<T> {
  const { maxHistory = 50, debounceMs = 300 } = options;

  // Past states (for undo)
  const [past, setPast] = useState<T[]>([]);
  // Future states (for redo)
  const [future, setFuture] = useState<T[]>([]);
  // Current state reference
  const currentRef = useRef<T>(initialState);
  // Debounce timer
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag to prevent pushing during undo/redo
  const isUndoRedoRef = useRef(false);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const pushState = useCallback((state: T) => {
    // Don't push if we're in the middle of undo/redo
    if (isUndoRedoRef.current) {
      currentRef.current = state;
      return;
    }

    // Debounce rapid changes
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setPast(prev => {
        // Don't push if state is the same (deep compare would be expensive, so we skip)
        const newPast = [...prev, currentRef.current];
        // Limit history size
        if (newPast.length > maxHistory) {
          return newPast.slice(newPast.length - maxHistory);
        }
        return newPast;
      });
      // Clear future on new change
      setFuture([]);
      currentRef.current = state;
    }, debounceMs);
  }, [maxHistory, debounceMs]);

  const undo = useCallback(() => {
    if (past.length === 0) return undefined;

    isUndoRedoRef.current = true;

    const previous = past[past.length - 1];
    const newPast = past.slice(0, past.length - 1);

    setPast(newPast);
    setFuture(prev => [currentRef.current, ...prev]);
    currentRef.current = previous;

    // Reset flag after a tick
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return previous;
  }, [past]);

  const redo = useCallback(() => {
    if (future.length === 0) return undefined;

    isUndoRedoRef.current = true;

    const next = future[0];
    const newFuture = future.slice(1);

    setFuture(newFuture);
    setPast(prev => [...prev, currentRef.current]);
    currentRef.current = next;

    // Reset flag after a tick
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return next;
  }, [future]);

  const clear = useCallback(() => {
    setPast([]);
    setFuture([]);
  }, []);

  return {
    pushState,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    clear,
    historySize: past.length + future.length,
  };
}
